import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {
    buildRunRequest, dbPerformanceRequest, defaultSelection, formatBytes, formatMs, groupTestsByCategory,
    isRunActive, summarizeComparison
} from './dbPerformanceApi';
import './DbPerformance.scss';

const categoryLabels = {
    LATEST: 'Latest values', SENSOR_HISTORY: 'One sensor over a period', MULTI_SENSOR: "User's sensors",
    TIME_SCAN: 'Time filters across all sensors', WRITE: 'Insert path'
};
const verdictLabels = {
    FASTER: 'Faster', SLOWER: 'Slower', SAME: 'No change', ONLY_BASE: 'Only in base run',
    ONLY_TARGET: 'Only in compared run', ERROR: 'Error'
};
const statusLabels = {
    QUEUED: 'Queued', RUNNING: 'Running', DONE: 'Done', CANCELLED: 'Cancelled', FAILED: 'Failed', OK: 'OK', ERROR: 'Error'
};
const POLL_MS = 3000;

const formatDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');
const formatPercent = value => (value === null || value === undefined ? '—'
    : `${value > 0 ? '+' : ''}${Number(value).toFixed(1)}%`);
const listText = value => (value ? String(value).split(',').join(', ') : '—');
const blank = value => value === null || value === undefined;

const ParameterSummary = ({parameterSet, t}) => {
    if (!parameterSet) return null;
    const p = parameterSet.parameters || {};
    return <dl className='dbPerfParameters'>
        <div><dt>{t.gettext('Reference time')}</dt><dd>{formatDate(p.referenceTime)}</dd></div>
        <div><dt>{t.gettext('Busiest sensor')}</dt><dd>{p.heavySensorId}</dd></div>
        <div><dt>{t.gettext('Typical sensor')}</dt><dd>{p.typicalSensorId}</dd></div>
        <div><dt>{t.gettext('Test user')}</dt>
            <dd>{p.userId} ({(p.userSensorIds || []).length} {t.gettext('sensors')})</dd></div>
        <div><dt>{t.gettext('Newest measurement id')}</dt><dd>{p.maxMeasurementId}</dd></div>
    </dl>;
};

const SnapshotView = ({snapshot, t}) => {
    if (!snapshot) return null;
    const object = value => (value && typeof value === 'object' && !Array.isArray(value) ? value : null);
    const rows = value => (Array.isArray(value) ? value : []);
    const m = object(snapshot.measurements);
    const db = object(snapshot.database);
    const settings = object(snapshot.settings);
    return <div className='dbPerfSnapshot'>
        {db && <p>{db.version} · {t.gettext('Database size')}: {formatBytes(db.sizeBytes)}</p>}
        {m && <dl className='dbPerfParameters'>
            <div><dt>{t.gettext('Measurements rows (estimate)')}</dt><dd>{Number(m.estimatedRows).toLocaleString()}</dd></div>
            <div><dt>{t.gettext('Table size')}</dt><dd>{formatBytes(m.tableBytes)}</dd></div>
            <div><dt>{t.gettext('Index size')}</dt><dd>{formatBytes(m.indexBytes)}</dd></div>
            <div><dt>{t.gettext('Data range')}</dt><dd>{formatDate(m.firstRowTime)} – {formatDate(m.lastRowTime)}</dd></div>
        </dl>}
        <h3>{t.gettext('Largest tables')}</h3>
        <div className='dbPerfTable'><table>
            <thead><tr><th>{t.gettext('Table')}</th><th>{t.gettext('Live rows')}</th><th>{t.gettext('Dead rows')}</th>
                <th>{t.gettext('Table size')}</th><th>{t.gettext('Index size')}</th>
                <th>{t.gettext('Sequential scans')}</th><th>{t.gettext('Index scans')}</th>
                <th>{t.gettext('Last analyze')}</th></tr></thead>
            <tbody>{rows(snapshot.tables).map(row => <tr key={row.tableName}>
                <td>{row.tableName}</td><td>{Number(row.nLiveTup).toLocaleString()}</td>
                <td>{Number(row.nDeadTup).toLocaleString()}</td><td>{formatBytes(row.tableBytes)}</td>
                <td>{formatBytes(row.indexBytes)}</td><td>{row.seqScan}</td><td>{row.idxScan}</td>
                <td>{formatDate(row.lastAutoanalyze || row.lastAnalyze)}</td>
            </tr>)}</tbody>
        </table></div>
        <h3>{t.gettext('Indexes')}</h3>
        <div className='dbPerfTable'><table>
            <thead><tr><th>{t.gettext('Table')}</th><th>{t.gettext('Index')}</th><th>{t.gettext('Size')}</th>
                <th>{t.gettext('Index scans')}</th><th>{t.gettext('Definition')}</th></tr></thead>
            <tbody>{rows(snapshot.indexes).map(row => <tr key={row.indexName}>
                <td>{row.tableName}</td><td>{row.indexName}</td><td>{formatBytes(row.indexBytes)}</td>
                <td>{row.idxScan}</td><td className='dbPerfCode'>{row.indexdef}</td>
            </tr>)}</tbody>
        </table></div>
        <h3>{t.gettext('Settings')}</h3>
        {settings && <dl className='dbPerfParameters'>
            {Object.keys(settings).map(name => <div key={name}><dt>{name}</dt><dd>{settings[name]}</dd></div>)}
        </dl>}
        <h3>{t.gettext('Heaviest statements on measurements')}</h3>
        {Array.isArray(snapshot.statements) ? <div className='dbPerfTable'><table>
            <thead><tr><th>{t.gettext('Query')}</th><th>{t.gettext('Calls')}</th><th>{t.gettext('Total')}</th>
                <th>{t.gettext('Mean')}</th></tr></thead>
            <tbody>{snapshot.statements.map((row, index) => <tr key={index}>
                <td className='dbPerfCode'>{row.query}</td><td>{row.calls}</td><td>{formatMs(row.totalMs)}</td>
                <td>{formatMs(row.meanMs)}</td>
            </tr>)}</tbody>
        </table></div> : <p className='dbPerfMuted'>{String(snapshot.statements || '—')}</p>}
    </div>;
};

const TimingChart = ({data, bars}) => (
    <div className='dbPerfChart'>
        <ResponsiveContainer width='100%' height={Math.max(220, data.length * 32)}>
            <BarChart data={data} layout='vertical' margin={{left: 20, right: 24}}>
                <CartesianGrid strokeDasharray='3 3'/>
                <XAxis type='number' scale='log' domain={['auto', 'auto']} allowDataOverflow unit=' ms'/>
                <YAxis type='category' dataKey='name' width={230}/>
                <Tooltip formatter={value => formatMs(value)}/>
                <Legend/>
                {bars.map(bar => <Bar key={bar.key} dataKey={bar.key} name={bar.name} fill={bar.color}/>)}
            </BarChart>
        </ResponsiveContainer>
    </div>
);

const RunDetails = ({run, t, onClose}) => {
    const [plan, setPlan] = useState(null);
    const [planError, setPlanError] = useState('');
    const [showSnapshot, setShowSnapshot] = useState(false);
    const results = run.results || [];
    const chartData = results.filter(r => r.status === 'OK' && r.medianMs > 0)
        .map(r => ({name: r.testKey, median: Number(r.medianMs), p95: Number(r.p95Ms)}));

    const openPlan = async testKey => {
        setPlanError('');
        try {
            setPlan(await dbPerformanceRequest(`/runs/${run.id}/plan?testKey=${encodeURIComponent(testKey)}`, {}));
        } catch (e) {
            setPlanError(t.gettext('Could not load the plan.'));
        }
    };

    return <section className='dbPerfCard' aria-label={t.gettext('Run details')}>
        <div className='dbPerfHeader'>
            <h2>{`${t.gettext('Results of run #')}${run.id}`}</h2>
            <button type='button' className='dbPerfSecondary' onClick={onClose}>{t.gettext('Close')}</button>
        </div>
        <p>{[run.label || '—', t.gettext(statusLabels[run.status] || run.status), run.parameterSetName,
            `${t.gettext('Iterations')}: ${run.iterations}`, `${t.gettext('Warm-up')}: ${run.warmup}`].join(' · ')}</p>
        {run.notes && <p className='dbPerfMuted'>{run.notes}</p>}
        {run.error && <p className='dbPerfError'>{run.error}</p>}
        {chartData.length > 0 && <TimingChart data={chartData} bars={[
            {key: 'median', name: t.gettext('Median'), color: '#338b37'},
            {key: 'p95', name: 'P95', color: '#a5c9a7'}]}/>}
        <div className='dbPerfTable'><table>
            <thead><tr><th>{t.gettext('Test')}</th><th>{t.gettext('Status')}</th><th>{t.gettext('Median')}</th>
                <th>P95</th><th>{t.gettext('Min')}</th><th>{t.gettext('Max')}</th><th>{t.gettext('Cold')}</th>
                <th>{t.gettext('Rows')}</th><th>{t.gettext('Plan root')}</th><th>{t.gettext('Indexes used')}</th>
                <th>{t.gettext('Sequential scans')}</th><th>{t.gettext('Blocks hit / read')}</th><th/></tr></thead>
            <tbody>{results.map(r => <tr key={r.testKey} className={r.status !== 'OK' ? 'dbPerfRowError' : ''}>
                <td><strong>{r.testName}</strong><div className='dbPerfMuted'>{r.testKey}</div></td>
                <td>{r.status === 'OK' ? t.gettext('OK') : <span className='dbPerfError'>{r.error || r.status}</span>}</td>
                <td>{formatMs(r.medianMs)}</td><td>{formatMs(r.p95Ms)}</td><td>{formatMs(r.minMs)}</td>
                <td>{formatMs(r.maxMs)}</td><td>{formatMs(r.coldMs)}</td>
                <td>{blank(r.rowsReturned) ? '—' : r.rowsReturned}</td>
                <td>{r.planRootNode || '—'}</td><td className='dbPerfCode'>{listText(r.indexesUsed)}</td>
                <td className={r.seqScans ? 'dbPerfWarn' : ''}>{listText(r.seqScans)}</td>
                <td>{blank(r.sharedHitBlocks) ? '—' : `${r.sharedHitBlocks} / ${r.sharedReadBlocks}`}</td>
                <td>{r.status === 'OK' && <button type='button' className='dbPerfSecondary'
                                                  onClick={() => openPlan(r.testKey)}>{t.gettext('Plan')}</button>}</td>
            </tr>)}</tbody>
        </table></div>
        {planError && <p className='dbPerfError'>{planError}</p>}
        {plan && <div className='dbPerfPlan'>
            <div className='dbPerfHeader'>
                <h3>{`${t.gettext('Execution plan')}: ${plan.testKey}`}</h3>
                <button type='button' className='dbPerfSecondary' onClick={() => setPlan(null)}>{t.gettext('Close')}</button>
            </div>
            <pre className='dbPerfCode'>{plan.sqlText}</pre>
            <pre className='dbPerfCode'>{JSON.stringify(plan.planJson, null, 2)}</pre>
        </div>}
        {run.snapshot && <>
            <button type='button' className='dbPerfSecondary' onClick={() => setShowSnapshot(!showSnapshot)}>
                {showSnapshot ? t.gettext('Hide database state') : t.gettext('Show database state at run start')}
            </button>
            {showSnapshot && <SnapshotView snapshot={run.snapshot} t={t}/>}
        </>}
    </section>;
};

const ComparePanel = ({runs, t}) => {
    const finished = runs.filter(run => !isRunActive(run));
    const [baseRunId, setBaseRunId] = useState('');
    const [targetRunId, setTargetRunId] = useState('');
    const [comparison, setComparison] = useState(null);
    const [error, setError] = useState('');
    const summary = useMemo(() => summarizeComparison(comparison ? comparison.rows : []), [comparison]);

    const compare = async () => {
        setError('');
        if (!baseRunId || !targetRunId || baseRunId === targetRunId) {
            setError(t.gettext('Select two different runs.'));
            return;
        }
        try {
            setComparison(await dbPerformanceRequest(`/compare?baseRunId=${baseRunId}&targetRunId=${targetRunId}`, {}));
        } catch (e) {
            setError(t.gettext('Could not compare the runs.'));
        }
    };
    const option = run => <option key={run.id} value={run.id}>
        {`#${run.id} ${run.label || ''} (${formatDate(run.createdAt)})`}</option>;
    const chartData = comparison ? comparison.rows.filter(r => r.baseMedianMs > 0 && r.targetMedianMs > 0)
        .map(r => ({name: r.testKey, base: r.baseMedianMs, target: r.targetMedianMs})) : [];
    const planText = r => (r.planChanged
        ? `${listText(r.baseIndexes)} / seq: ${listText(r.baseSeqScans)} → ${listText(r.targetIndexes)} / seq: ${listText(r.targetSeqScans)}`
        : t.gettext('unchanged'));

    return <section className='dbPerfCard'>
        <div className='dbPerfForm'>
            <label htmlFor='dbPerfBase'>{t.gettext('Base run')}</label>
            <select id='dbPerfBase' value={baseRunId} onChange={e => setBaseRunId(e.target.value)}>
                <option value=''>—</option>
                {finished.map(option)}
            </select>
            <label htmlFor='dbPerfTarget'>{t.gettext('Compared run')}</label>
            <select id='dbPerfTarget' value={targetRunId} onChange={e => setTargetRunId(e.target.value)}>
                <option value=''>—</option>
                {finished.map(option)}
            </select>
        </div>
        <button type='button' onClick={compare}>{t.gettext('Compare runs')}</button>
        {error && <p className='dbPerfError'>{error}</p>}
        {comparison && <>
            {!comparison.sameParameterSet && <p className='dbPerfWarn'>
                {t.gettext('The runs used different parameter sets, so the timings are not directly comparable.')}</p>}
            <p className='dbPerfSummary'>
                <span className='dbPerfFaster'>{`${t.gettext('Faster')}: ${summary.faster}`}</span>
                <span className='dbPerfSlower'>{`${t.gettext('Slower')}: ${summary.slower}`}</span>
                <span>{`${t.gettext('No change')}: ${summary.same}`}</span>
                <span>{`${t.gettext('Plan changed')}: ${summary.planChanged}`}</span>
                <span>{`${t.gettext('Sum of medians')}: ${formatMs(summary.baseTotalMs)} → ${formatMs(summary.targetTotalMs)}`}</span>
            </p>
            {chartData.length > 0 && <TimingChart data={chartData} bars={[
                {key: 'base', name: `#${comparison.baseRunId}`, color: '#9aa5b1'},
                {key: 'target', name: `#${comparison.targetRunId}`, color: '#338b37'}]}/>}
            <div className='dbPerfTable'><table>
                <thead><tr><th>{t.gettext('Test')}</th><th>{t.gettext('Verdict')}</th>
                    <th>{t.gettext('Base median')}</th><th>{t.gettext('Compared median')}</th>
                    <th>{t.gettext('Change')}</th><th>{t.gettext('Speed-up')}</th>
                    <th>{t.gettext('Plan (base → compared)')}</th></tr></thead>
                <tbody>{comparison.rows.map(r => <tr key={r.testKey} className={`dbPerfVerdict${r.verdict}`}>
                    <td>{r.testKey}</td><td>{t.gettext(verdictLabels[r.verdict] || r.verdict)}</td>
                    <td>{formatMs(r.baseMedianMs)}</td><td>{formatMs(r.targetMedianMs)}</td>
                    <td>{formatPercent(r.deltaPercent)}</td>
                    <td>{r.speedup ? `${Number(r.speedup).toFixed(1)}×` : '—'}</td>
                    <td className='dbPerfCode'>{planText(r)}</td>
                </tr>)}</tbody>
            </table></div>
        </>}
    </section>;
};

const TestSelection = ({groups, selected, onToggle, t}) => groups.map(group =>
    <fieldset key={group.category} className='dbPerfGroup'>
        <legend>{t.gettext(categoryLabels[group.category] || group.category)}</legend>
        {group.tests.map(test => <div key={test.key} className='dbPerfTest'>
            <input type='checkbox' id={`dbPerfTest-${test.key}`} checked={selected.has(test.key)}
                   onChange={() => onToggle(test.key)}/>
            <div>
                <label htmlFor={`dbPerfTest-${test.key}`}>{test.name}</label>
                {test.heavy && <span className='dbPerfBadge'>{t.gettext('Heavy')}</span>}
                {test.mode === 'WRITE_ROLLBACK' && <span className='dbPerfBadge'>{t.gettext('Rolled back')}</span>}
                <div className='dbPerfMuted'>{test.description}</div>
                <details><summary>SQL</summary><pre className='dbPerfCode'>{test.sql}</pre></details>
            </div>
        </div>)}
    </fieldset>);

const DbPerformance = () => {
    const t = useSelector(state => state.language.translator);
    const [tab, setTab] = useState('runs');
    const [catalog, setCatalog] = useState(null);
    const [parameterSets, setParameterSets] = useState([]);
    const [runs, setRuns] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [form, setForm] = useState({parameterSetId: '', label: '', notes: '', iterations: '10', warmup: '2', timeoutSeconds: '60'});
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [details, setDetails] = useState(null);
    const [snapshot, setSnapshot] = useState(null);

    const loadRuns = useCallback(async () => {
        const list = (await dbPerformanceRequest('/runs', {})) || [];
        setRuns(list);
        return list;
    }, []);

    useEffect(() => {
        let cancelled = false;
        Promise.all([dbPerformanceRequest('/catalog', {}), dbPerformanceRequest('/parameter_sets', {}), loadRuns()])
            .then(([catalogResponse, sets]) => {
                if (cancelled) return;
                setCatalog(catalogResponse);
                setSelected(defaultSelection(catalogResponse.tests));
                setParameterSets(sets || []);
                setForm(current => ({
                    ...current,
                    parameterSetId: sets && sets.length ? String(sets[0].id) : '',
                    iterations: String(catalogResponse.defaultIterations),
                    warmup: String(catalogResponse.defaultWarmup),
                    timeoutSeconds: String(catalogResponse.defaultStatementTimeoutMs / 1000)
                }));
            })
            .catch(() => !cancelled && setError(t.gettext('Could not load the performance data.')));
        return () => {
            cancelled = true;
        };
        // load once; the translator only formats the error message
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadRuns]);

    const anyActive = runs.some(isRunActive);
    useEffect(() => {
        if (!anyActive) return undefined;
        const timer = setInterval(() => {
            loadRuns().catch(() => null);
            if (details) dbPerformanceRequest(`/runs/${details.id}`, {}).then(setDetails).catch(() => null);
        }, POLL_MS);
        return () => clearInterval(timer);
    }, [anyActive, loadRuns, details]);

    const groups = useMemo(() => groupTestsByCategory(catalog ? catalog.tests : []), [catalog]);
    const parameterSet = parameterSets.find(set => String(set.id) === form.parameterSetId);
    const update = field => event => setForm({...form, [field]: event.target.value});
    const toggle = key => {
        const next = new Set(selected);
        if (next.has(key)) next.delete(key); else next.add(key);
        setSelected(next);
    };
    const attempt = async (action, failure) => {
        setError('');
        try {
            await action();
        } catch (e) {
            setError(t.gettext(failure));
        }
    };

    const detectParameterSet = async () => {
        setBusy(true);
        await attempt(async () => {
            const created = await dbPerformanceRequest('/parameter_sets/resolve', {method: 'POST'});
            setParameterSets((await dbPerformanceRequest('/parameter_sets', {})) || []);
            setForm(current => ({...current, parameterSetId: String(created.id)}));
        }, 'Could not detect parameters.');
        setBusy(false);
    };

    const startRun = async () => {
        setMessage('');
        setError('');
        let request;
        try {
            request = buildRunRequest(form, selected, catalog ? catalog.tests : []);
        } catch (e) {
            setError(t.gettext(e.message));
            return;
        }
        setBusy(true);
        await attempt(async () => {
            await dbPerformanceRequest('/runs', {method: 'POST', body: request});
            setMessage(t.gettext('Run started'));
            await loadRuns();
        }, 'Could not start the run.');
        setBusy(false);
    };

    const openDetails = id => attempt(async () => setDetails(await dbPerformanceRequest(`/runs/${id}`, {})),
        'Could not load the run.');
    const cancelRun = id => attempt(async () => {
        await dbPerformanceRequest(`/runs/${id}/cancel`, {method: 'POST'});
        await loadRuns();
    }, 'Could not cancel the run.');
    const deleteRun = id => attempt(async () => {
        await dbPerformanceRequest(`/runs/${id}`, {method: 'DELETE'});
        if (details && details.id === id) setDetails(null);
        await loadRuns();
    }, 'Could not delete the run.');
    const loadSnapshot = async () => {
        setBusy(true);
        await attempt(async () => setSnapshot(await dbPerformanceRequest('/snapshot/current', {})),
            'Could not load the database state.');
        setBusy(false);
    };

    return <div className='dbPerformance'>
        <h1>{t.gettext('Database performance')}</h1>
        <p className='dbPerfMuted'>{t.gettext('Runs real measurement queries against this database in rolled-back, read-only transactions and stores the timings, so an optimization can be measured before and after.')}</p>
        <div className='dbPerfTabs' role='tablist'>
            <button type='button' role='tab' aria-selected={tab === 'runs'} onClick={() => setTab('runs')}>{t.gettext('Runs')}</button>
            <button type='button' role='tab' aria-selected={tab === 'compare'} onClick={() => setTab('compare')}>{t.gettext('Compare')}</button>
            <button type='button' role='tab' aria-selected={tab === 'database'} onClick={() => setTab('database')}>{t.gettext('Database')}</button>
        </div>
        {error && <p className='dbPerfError' role='alert'>{error}</p>}
        {message && <p className='dbPerfSuccess'>{message}</p>}

        {tab === 'runs' && <>
            <section className='dbPerfCard'>
                <h2>{t.gettext('New run')}</h2>
                <div className='dbPerfForm'>
                    <label htmlFor='dbPerfParameterSet'>{t.gettext('Parameter set')}</label>
                    <div className='dbPerfInline'>
                        <select id='dbPerfParameterSet' value={form.parameterSetId} onChange={update('parameterSetId')}>
                            <option value=''>—</option>
                            {parameterSets.map(set => <option key={set.id} value={set.id}>{`#${set.id} ${set.name}`}</option>)}
                        </select>
                        <button type='button' className='dbPerfSecondary' disabled={busy} onClick={detectParameterSet}>
                            {t.gettext('Detect new parameter set')}</button>
                    </div>
                    <label htmlFor='dbPerfLabel'>{t.gettext('Label')}</label>
                    <input id='dbPerfLabel' value={form.label} onChange={update('label')}
                           placeholder={t.gettext('e.g. baseline, after BRIN index')}/>
                    <label htmlFor='dbPerfNotes'>{t.gettext('Notes')}</label>
                    <input id='dbPerfNotes' value={form.notes} onChange={update('notes')}/>
                    <label htmlFor='dbPerfIterations'>{t.gettext('Iterations')}</label>
                    <input id='dbPerfIterations' type='number' value={form.iterations} onChange={update('iterations')}/>
                    <label htmlFor='dbPerfWarmup'>{t.gettext('Warm-up')}</label>
                    <input id='dbPerfWarmup' type='number' value={form.warmup} onChange={update('warmup')}/>
                    <label htmlFor='dbPerfTimeout'>{t.gettext('Statement timeout (s)')}</label>
                    <input id='dbPerfTimeout' type='number' value={form.timeoutSeconds} onChange={update('timeoutSeconds')}/>
                </div>
                <ParameterSummary parameterSet={parameterSet} t={t}/>
                <h3>{t.gettext('Tests')}</h3>
                <p className='dbPerfMuted'>{t.gettext('Heavy tests scan large parts of the table; run them outside peak hours. Insert tests are always rolled back.')}</p>
                <TestSelection groups={groups} selected={selected} onToggle={toggle} t={t}/>
                <button type='button' disabled={busy || !catalog} onClick={startRun}>{t.gettext('Start run')}</button>
            </section>

            <section className='dbPerfCard'>
                <h2>{t.gettext('Runs')}</h2>
                <div className='dbPerfTable'><table>
                    <thead><tr><th>#</th><th>{t.gettext('Label')}</th><th>{t.gettext('Status')}</th>
                        <th>{t.gettext('Progress')}</th><th>{t.gettext('Parameter set')}</th>
                        <th>{t.gettext('Created')}</th><th>{t.gettext('Sum of medians')}</th><th/></tr></thead>
                    <tbody>{runs.map(run => <tr key={run.id}>
                        <td>{run.id}</td><td>{run.label || '—'}</td>
                        <td>{t.gettext(statusLabels[run.status] || run.status)}</td>
                        <td>{`${run.testsDone}/${run.testsTotal}`}</td><td>{run.parameterSetName}</td>
                        <td>{formatDate(run.createdAt)}</td><td>{formatMs(run.totalMedianMs)}</td>
                        <td className='dbPerfActions'>
                            <button type='button' className='dbPerfSecondary' onClick={() => openDetails(run.id)}>{t.gettext('Details')}</button>
                            {isRunActive(run)
                                ? <button type='button' className='dbPerfSecondary' onClick={() => cancelRun(run.id)}>{t.gettext('Cancel')}</button>
                                : <button type='button' className='dbPerfDanger' onClick={() => deleteRun(run.id)}>{t.gettext('Delete')}</button>}
                        </td>
                    </tr>)}</tbody>
                </table></div>
                {runs.length === 0 && <p className='dbPerfMuted'>{t.gettext('No runs yet.')}</p>}
            </section>
            {details && <RunDetails key={details.id} run={details} t={t} onClose={() => setDetails(null)}/>}
        </>}

        {tab === 'compare' && <ComparePanel runs={runs} t={t}/>}

        {tab === 'database' && <section className='dbPerfCard'>
            <button type='button' disabled={busy} onClick={loadSnapshot}>{t.gettext('Load current database state')}</button>
            <SnapshotView snapshot={snapshot} t={t}/>
        </section>}
    </div>;
};

export default DbPerformance;
